<template>
  <main class="supplier-models-page">
    <section class="soft-card table-panel">
      <div class="toolbar">
        <el-button size="small" plain icon="el-icon-arrow-left" circle @click="goBack" style="margin-right: 4px;" />
        <el-input
          v-model="searchQuery"
          placeholder="搜索模型名称..."
          size="small"
          clearable
          class="search-input"
          @keyup.enter.native="onSearch" @clear="onSearch"
        />
        <el-button size="small" plain icon="el-icon-search" @click="onSearch">搜索</el-button>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="onAdd">新增</el-button>
      </div>
      <div class="desktop-only">
        <el-table :data="allModels" size="small" style="width: 100%">
          <el-table-column type="index" label="序号" width="60" :index="pageIndex" />
          <el-table-column label="模型名称" min-width="240">
            <template slot-scope="scope">
              <div class="model-cell">
                <span class="model-icon">
                  <img v-if="scope.row.modelIcon" :src="scope.row.modelIcon" :alt="scope.row.name" @error="scope.row.modelIcon = ''">
                  <span v-else>{{ modelAbbr(scope.row.name) }}</span>
                </span>
                <span class="model-name">{{ scope.row.name }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="类型" width="90">
            <template slot-scope="scope">
              <el-tag size="small" :type="modelTypeInfo(scope.row.modelType).tagType">
                {{ modelTypeInfo(scope.row.modelType).label }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="价格">
            <template slot-scope="scope">
              <template v-if="scope.row.modelType === 'IMAGE'">
                ¥ {{ formatPrice(scope.row.outputPrice) }} / 张
              </template>
              <template v-else-if="scope.row.modelType === 'ASR'">
                输入 ¥ {{ formatPrice(scope.row.inputPrice) }} / 千秒
              </template>
              <template v-else-if="scope.row.modelType === 'EMBEDDING' || scope.row.modelType === 'RERANK'">
                输入 ¥ {{ formatPrice(scope.row.inputPrice) }} / 1K tokens
              </template>
              <template v-else>
                <div>输入 ¥ {{ formatPrice(scope.row.inputPrice) }} / 1K tokens</div>
                <div>输出 ¥ {{ formatPrice(scope.row.outputPrice) }} / 1K tokens</div>
                <div>缓存 ¥ {{ formatPrice(scope.row.cachePrice) }} / 1K tokens</div>
              </template>
            </template>
          </el-table-column>
          <el-table-column label="TPM 上限">
            <template slot-scope="scope">
              <span v-if="scope.row.tpmLimitPerMinute">{{ formatTpm(scope.row.tpmLimitPerMinute) }}</span>
              <span v-else style="color:var(--muted);">不限制</span>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="200">
            <template slot-scope="scope">
              <el-tag size="small" :type="offlineStatusType(scope.row)">{{ offlineStatusText(scope.row) }}</el-tag>
              <span v-if="scope.row.offlineAt" style="margin-left:6px;font-size:12px;color:var(--muted);">{{ $formatTime(scope.row.offlineAt) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="创建时间" width="180">
            <template slot-scope="scope">{{ $formatTime(scope.row.createdAt) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="160">
            <template slot-scope="scope">
              <el-button size="small" type="text" @click="onEdit(scope.row)">编辑</el-button>
              <el-button size="small" type="text" class="delete-btn" @click="onDelete(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="mobile-only list-cards">
        <div class="list-card" v-for="item in allModels" :key="item.id">
          <div class="card-header">
            <div class="model-cell" style="display:inline-flex;align-items:center;gap:8px;">
              <span class="model-icon" style="width:24px;height:24px;font-size:11px;">
                <img v-if="item.modelIcon" :src="item.modelIcon" :alt="item.name" @error="item.modelIcon = ''">
                <span v-else>{{ modelAbbr(item.name) }}</span>
              </span>
              <div class="card-title">{{ item.name }}</div>
            </div>
          </div>
          <div class="price-grid">
            <template v-if="item.modelType === 'IMAGE'">
              <div class="price-cell">
                <div class="label">每张价格</div>
                <div class="value output">¥ {{ formatPrice(item.outputPrice) }}</div>
              </div>
            </template>
            <template v-else-if="item.modelType === 'ASR'">
              <div class="price-cell">
                <div class="label">输入（元/千秒）</div>
                <div class="value input">¥ {{ formatPrice(item.inputPrice) }}</div>
              </div>
            </template>
            <template v-else-if="item.modelType === 'EMBEDDING' || item.modelType === 'RERANK'">
              <div class="price-cell">
                <div class="label">输入（元/1K tokens）</div>
                <div class="value input">¥ {{ formatPrice(item.inputPrice) }}</div>
              </div>
            </template>
            <template v-else>
              <div class="price-cell">
                <div class="label">输入</div>
                <div class="value input">¥ {{ formatPrice(item.inputPrice) }}</div>
              </div>
              <div class="price-cell">
                <div class="label">输出</div>
                <div class="value output">¥ {{ formatPrice(item.outputPrice) }}</div>
              </div>
              <div class="price-cell">
                <div class="label">缓存</div>
                <div class="value cache">¥ {{ formatPrice(item.cachePrice) }}</div>
              </div>
            </template>
          </div>
          <div class="meta-row">
            <el-tag size="small" :type="modelTypeInfo(item.modelType).tagType">
              {{ modelTypeInfo(item.modelType).label }}
            </el-tag>
            <span v-if="item.tpmLimitPerMinute">TPM {{ formatTpm(item.tpmLimitPerMinute) }}</span>
            <span v-else>TPM 不限制</span>
            <span>
              <el-tag size="small" :type="offlineStatusType(item)">{{ offlineStatusText(item) }}</el-tag>
            </span>
            <span>{{ $formatTime(item.createdAt) }}</span>
          </div>
          <div class="card-actions">
            <el-button size="small" type="text" @click="onEdit(item)">编辑</el-button>
            <el-button size="small" type="text" class="delete-btn" @click="onDelete(item)">删除</el-button>
          </div>
        </div>
      </div>
      <el-pagination
        class="pagination-bar"
        layout="total, prev, pager, next"
        small
        :total="total"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="onPageChange"
      />
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="620px" :close-on-click-modal="false" top="15vh">
        <div style="margin-bottom:20px;font-size:13px;color:var(--muted);">
          <i class="el-icon-s-platform" style="margin-right:4px;"></i>
          {{ supplierName }}
        </div>
        <el-form ref="form" :model="form" :rules="rules" label-width="0" size="small">
          <el-form-item prop="name">
            <el-select v-model="form.name" filterable placeholder="请选择模型" style="width: 100%">
              <el-option
                v-for="model in modelCatalog"
                :key="model.id"
                :label="model.name"
                :value="model.name"
              >
                <div class="model-option">
                  <span class="model-icon small">
                    <img v-if="model.icon" :src="model.icon" :alt="model.name" @error="model.icon = ''">
                    <span v-else>{{ modelAbbr(model.name) }}</span>
                  </span>
                  <span>{{ model.name }}</span>
                </div>
              </el-option>
            </el-select>
          </el-form-item>
          <el-form-item prop="modelType" style="margin-top:12px;">
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">模型类型（文本按 1K tokens 计费，图片按张计费，向量/重排按 1K tokens 计输入，语音按千秒计输入）</div>
            <el-radio-group v-model="form.modelType">
              <el-radio-button label="TEXT">文本模型</el-radio-button>
              <el-radio-button label="IMAGE">图片模型</el-radio-button>
              <el-radio-button label="EMBEDDING">向量模型</el-radio-button>
              <el-radio-button label="RERANK">重排模型</el-radio-button>
              <el-radio-button label="ASR">语音识别模型</el-radio-button>
            </el-radio-group>
          </el-form-item>
          <div v-if="form.modelType === 'IMAGE'" style="display:flex;gap:12px;">
            <el-form-item prop="outputPrice" style="flex:1;">
              <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">每张价格（元/张）</div>
              <el-input v-model="form.outputPrice" placeholder="0.00">
                <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
                <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">/张</span></template>
              </el-input>
            </el-form-item>
          </div>
          <div v-else-if="form.modelType === 'EMBEDDING' || form.modelType === 'RERANK' || form.modelType === 'ASR'" style="display:flex;gap:12px;">
            <el-form-item prop="inputPrice" style="flex:1;">
              <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">输入价格</div>
              <el-input v-model="form.inputPrice" placeholder="0.00">
                <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
                <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">{{ form.modelType === 'ASR' ? '/千秒' : '/1K tokens' }}</span></template>
              </el-input>
            </el-form-item>
          </div>
          <div v-else style="display:flex;gap:12px;">
            <el-form-item prop="inputPrice" style="flex:1;">
              <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">输入价格</div>
              <el-input v-model="form.inputPrice" placeholder="0.00">
                <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
                <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">/1K tokens</span></template>
              </el-input>
            </el-form-item>
            <el-form-item prop="outputPrice" style="flex:1;">
              <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">输出价格</div>
              <el-input v-model="form.outputPrice" placeholder="0.00">
                <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
                <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">/1K tokens</span></template>
              </el-input>
            </el-form-item>
            <el-form-item prop="cachePrice" style="flex:1;">
              <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">缓存价格</div>
              <el-input v-model="form.cachePrice" placeholder="0.00">
                <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
                <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">/1K tokens</span></template>
              </el-input>
            </el-form-item>
          </div>
          <el-form-item v-if="form.modelType === 'TEXT'" prop="tpmLimitPerMinute" style="margin-top:12px;">
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">TPM 上限（tokens/分钟，留空 = 不限制）</div>
            <el-input v-model="form.tpmLimitPerMinute" placeholder="留空表示该模型不限制 TPM">
              <template slot="suffix"><span style="font-size:12px;color:var(--muted-2);">tokens/min</span></template>
            </el-input>
          </el-form-item>
          <el-form-item prop="offlineAt" style="margin-top:12px;">
            <div style="margin-bottom:4px;font-size:12px;color:var(--muted);">下架时间（到点后模型自动禁用并从用户端隐藏，留空 = 永不下架）</div>
            <el-date-picker
              v-model="form.offlineAt"
              type="date"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              placeholder="选择下架日期"
              style="width:100%"
              size="small"
            ></el-date-picker>
          </el-form-item>
        </el-form>
        <template v-if="form.modelType === 'TEXT'">
          <el-divider style="margin: 16px 0;" />
          <div style="margin-bottom:8px;font-size:13px;color:var(--text);font-weight:700;">
            分时段价格
            <span style="font-weight:400;color:var(--muted);font-size:12px;margin-left:6px;">未配置时段按上方基准价计费（空闲时段），最多 5 段，支持跨午夜</span>
          </div>
          <div v-for="(slot, index) in timePrices" :key="index" style="padding:10px;border:1px solid var(--line-soft);border-radius:8px;margin-bottom:10px;background:rgba(255,255,255,0.03);">
            <div style="display:flex;gap:6px;align-items:center;">
              <el-time-picker v-model="slot.startTime" value-format="HH:mm" format="HH:mm" placeholder="开始" style="width:120px;"></el-time-picker>
              <span style="color:var(--muted-2);">至</span>
              <el-time-picker v-model="slot.endTime" value-format="HH:mm" format="HH:mm" placeholder="结束" style="width:120px;"></el-time-picker>
              <el-button type="text" size="small" class="delete-btn" style="margin-left:auto;" @click="removeTimePrice(index)">删除</el-button>
            </div>
            <div style="display:flex;gap:6px;margin-top:8px;">
              <el-input v-model="slot.inputPrice" placeholder="输入价" size="small">
                <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
              </el-input>
              <el-input v-model="slot.outputPrice" placeholder="输出价" size="small">
                <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
              </el-input>
              <el-input v-model="slot.cachePrice" placeholder="缓存价" size="small">
                <template slot="prefix"><span style="color:var(--muted-2);">¥</span></template>
              </el-input>
            </div>
          </div>
          <el-button v-if="timePrices.length < 5" type="primary" plain size="mini" icon="el-icon-plus" @click="addTimePrice">添加时段</el-button>
        </template>
        <span slot="footer">
          <el-button @click="dialogVisible = false" size="small">取消</el-button>
          <el-button type="primary" @click="submitForm" size="small" :loading="submitting">确定</el-button>
        </span>
      </el-dialog>
    </section>
  </main>
</template>

<script>
import { modelTypeInfo as mtInfo } from '../utils/format'

export default {
  name: 'SupplierModelsPage',
  data() {
    return {
      allModels: [],
      modelCatalog: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      searchQuery: '',
      dialogVisible: false,
      dialogTitle: '',
      submitting: false,
      form: {
        id: null,
        name: '',
        modelType: 'TEXT',
        inputPrice: '',
        outputPrice: '',
        cachePrice: '',
        tpmLimitPerMinute: '',
        offlineAt: null
      },
      timePrices: [],
      rules: {
        name: [
          { required: true, message: '请选择模型', trigger: 'change' }
        ],
        modelType: [
          { required: true, message: '请选择模型类型', trigger: 'change' }
        ],
        inputPrice: [
          { pattern: /^\d+(\.\d+)?$/, message: '请输入有效数字', trigger: 'blur' }
        ],
        outputPrice: [
          { pattern: /^\d+(\.\d+)?$/, message: '请输入有效数字', trigger: 'blur' }
        ],
        cachePrice: [
          { pattern: /^\d+(\.\d+)?$/, message: '请输入有效数字', trigger: 'blur' }
        ],
        tpmLimitPerMinute: [
          { pattern: /^\d*$/, message: '请输入正整数或不填', trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    supplierId() {
      return this.$route.params.id
    },
    supplierName() {
      return this.$route.query.name || ''
    },
    pageIndex() {
      return (this.currentPage - 1) * this.pageSize + 1
    }
  },
  mounted() {
    this.fetchModels()
    this.fetchModelCatalog()
  },
  methods: {
    modelAbbr(name) {
      if (!name) return 'M'
      return name.substring(0, 2).toUpperCase()
    },
    modelTypeInfo(type) {
      return mtInfo(type)
    },
    formatPrice(value) {
      return new Intl.NumberFormat('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6
      }).format(value)
    },
    formatTpm(value) {
      if (!value) return ''
      return new Intl.NumberFormat('zh-CN').format(value) + ' / min'
    },
    offlineStatusType(row) {
      if (!row.offlineAt) return 'success'
      return new Date(row.offlineAt).getTime() <= Date.now() ? 'danger' : 'warning'
    },
    offlineStatusText(row) {
      if (!row.offlineAt) return '上架中'
      return new Date(row.offlineAt).getTime() <= Date.now() ? '已下架' : '即将下架'
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchModels()
    },
    onSearch() {
      this.currentPage = 1
      this.fetchModels()
    },
    goBack() {
      this.$router.push('/suppliers')
    },
    async fetchModels() {
      try {
        const res = await this.$http.get('/admin/suppliers/' + this.supplierId + '/models', {
          params: {
            keyword: this.searchQuery || undefined,
            page: this.currentPage,
            pageSize: this.pageSize
          }
        })
        this.allModels = res.records || []
        this.total = Number(res.total) || 0
      } catch {
        this.$message.error('加载模型列表失败')
      }
    },
    async fetchModelCatalog() {
      try {
        const res = await this.$http.get('/admin/model-catalog', {
          params: { page: 1, pageSize: 1000 }
        })
        this.modelCatalog = res.records || []
      } catch {
        this.modelCatalog = []
      }
    },
    onAdd() {
      if (this.modelCatalog.length === 0) {
        this.$message.warning('请先在模型库中维护模型')
        return
      }
      this.dialogTitle = '新增模型'
      this.form = { id: null, name: '', modelType: 'TEXT', inputPrice: '', outputPrice: '', cachePrice: '', tpmLimitPerMinute: '', offlineAt: null }
      this.timePrices = []
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form?.clearValidate())
    },
    onEdit(row) {
      this.dialogTitle = '编辑模型'
      this.form = {
        id: row.id,
        name: row.name,
        modelType: ['TEXT', 'IMAGE', 'EMBEDDING', 'RERANK', 'ASR'].indexOf(row.modelType) >= 0 ? row.modelType : 'TEXT',
        inputPrice: String(row.inputPrice ?? ''),
        outputPrice: String(row.outputPrice ?? ''),
        cachePrice: String(row.cachePrice ?? ''),
        tpmLimitPerMinute: row.tpmLimitPerMinute != null ? String(row.tpmLimitPerMinute) : '',
        offlineAt: row.offlineAt || null
      }
      this.timePrices = []
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.form?.clearValidate())
      this.fetchTimePrices(row.id)
    },
    async fetchTimePrices(modelId) {
      try {
        const res = await this.$http.get('/admin/suppliers/' + this.supplierId + '/models/' + modelId + '/time-prices')
        this.timePrices = (res || []).map(item => ({
          startTime: this.minutesToHHmm(item.startMinute),
          endTime: this.minutesToHHmm(item.endMinute, true),
          inputPrice: String(item.inputPrice ?? ''),
          outputPrice: String(item.outputPrice ?? ''),
          cachePrice: String(item.cachePrice ?? '')
        }))
      } catch {
        this.timePrices = []
      }
    },
    addTimePrice() {
      if (this.timePrices.length >= 5) return
      this.timePrices.push({ startTime: '', endTime: '', inputPrice: '', outputPrice: '', cachePrice: '' })
    },
    removeTimePrice(index) {
      this.timePrices.splice(index, 1)
    },
    minutesToHHmm(minutes, isEnd = false) {
      if (minutes == null) return ''
      const total = Number(minutes)
      if (isEnd && total === 1440) return '00:00'
      if (total < 0 || total >= 24 * 60) return ''
      const h = String(Math.floor(total / 60)).padStart(2, '0')
      const m = String(total % 60).padStart(2, '0')
      return h + ':' + m
    },
    hhmmToMinutes(v, isEnd = false) {
      if (!v) return null
      const m = v.split(':')
      let total = parseInt(m[0], 10) * 60 + parseInt(m[1], 10)
      if (isEnd && total === 0) return 1440
      return total
    },
    validateTimeSlots() {
      for (const slot of this.timePrices) {
        if (!slot.startTime || !slot.endTime) {
          this.$message.error('时段需同时设置开始与结束时间')
          return false
        }
        if (!/^\d+(\.\d+)?$/.test(slot.inputPrice || '') || !/^\d+(\.\d+)?$/.test(slot.outputPrice || '') || !/^\d+(\.\d+)?$/.test(slot.cachePrice || '')) {
          this.$message.error('时段价格请输入有效数字')
          return false
        }
      }
      const parsed = this.timePrices.map(s => ({
        start: this.hhmmToMinutes(s.startTime),
        end: this.hhmmToMinutes(s.endTime, true)
      }))
      for (const p of parsed) {
        if (p.start == null || p.start < 0 || p.start >= 1440 || p.end == null || p.end <= 0 || p.end > 1440 || p.start === p.end) {
          this.$message.error('时段时间不合法：开始 00:00-23:59，结束 00:01-24:00')
          return false
        }
      }
      for (let i = 0; i < parsed.length; i++) {
        for (let j = i + 1; j < parsed.length; j++) {
          if (this.slotsOverlap(parsed[i], parsed[j])) {
            this.$message.error('时段存在重叠，请检查后重试')
            return false
          }
        }
      }
      return true
    },
    inSlot(point, start, end) {
      return end > start ? (point >= start && point < end) : (point >= start || point < end)
    },
    slotsOverlap(a, b) {
      return this.inSlot(a.start, b.start, b.end) || this.inSlot(b.start, a.start, a.end)
    },
    submitForm() {
      if (!this.validateTimeSlots()) return
      this.$refs.form.validate(async valid => {
        if (!valid) return
        this.submitting = true
        try {
          const mt = this.form.modelType || 'TEXT'
          const isTextType = mt === 'TEXT'
          const isImageType = mt === 'IMAGE'
          const isInputOnly = mt === 'EMBEDDING' || mt === 'RERANK' || mt === 'ASR'
          const payload = {
            name: this.form.name,
            modelType: mt,
            inputPrice: isImageType ? 0 : (parseFloat(this.form.inputPrice) || 0),
            outputPrice: isTextType || isImageType ? (parseFloat(this.form.outputPrice) || 0) : 0,
            cachePrice: isTextType ? (parseFloat(this.form.cachePrice) || 0) : 0,
            tpmLimitPerMinute: isTextType && this.form.tpmLimitPerMinute
              ? parseInt(this.form.tpmLimitPerMinute, 10)
              : null,
            offlineAt: this.form.offlineAt || null
          }
          const timePricesPayload = isTextType
            ? this.timePrices.map(slot => ({
                startMinute: this.hhmmToMinutes(slot.startTime),
                endMinute: this.hhmmToMinutes(slot.endTime, true),
                inputPrice: parseFloat(slot.inputPrice) || 0,
                outputPrice: parseFloat(slot.outputPrice) || 0,
                cachePrice: parseFloat(slot.cachePrice) || 0
              }))
            : []
          let savedId = this.form.id
          if (this.form.id) {
            await this.$http.put('/admin/suppliers/' + this.supplierId + '/models/' + this.form.id, payload)
            await this.$http.put('/admin/suppliers/' + this.supplierId + '/models/' + this.form.id + '/time-prices', timePricesPayload)
            this.$message.success('编辑成功')
          } else {
            const created = await this.$http.post('/admin/suppliers/' + this.supplierId + '/models', payload)
            savedId = created?.id
            if (savedId && timePricesPayload.length) {
              await this.$http.put('/admin/suppliers/' + this.supplierId + '/models/' + savedId + '/time-prices', timePricesPayload)
            }
            this.$message.success('新增成功')
          }
          this.dialogVisible = false
          this.fetchModels()
        } catch {
          this.$message.error('操作失败')
        } finally {
          this.submitting = false
        }
      })
    },
    onDelete(row) {
      this.$confirm('确定删除模型「' + row.name + '」？', '确认删除', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        return this.$http.delete('/admin/suppliers/' + this.supplierId + '/models/' + row.id)
      }).then(() => {
        this.$message.success('删除成功')
        this.fetchModels()
      }).catch(e => {
        if (e === 'cancel' || e === 'close') return
        this.$message.error(e.response?.data?.error || '删除失败')
      })
    }
  }
}
</script>

<style scoped>
.supplier-models-page {
  padding: 18px 24px 76px;
}

.table-panel {
  padding: 16px;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}

.table-panel ::v-deep .el-table,
.table-panel ::v-deep .el-table__header-wrapper,
.table-panel ::v-deep .el-table__body-wrapper {
  border-radius: var(--radius);
}

.table-panel ::v-deep th {
  background: rgba(255, 255, 255, 0.045);
  color: #9eb1ca;
  font-weight: 850;
}

.table-panel ::v-deep td,
.table-panel ::v-deep th {
  border-color: var(--line-soft);
}

.pagination-bar {
  margin-top: 20px;
  text-align: right;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}

.search-input {
  width: 200px;
}

.model-cell,
.model-option {
  display: flex;
  align-items: center;
  gap: 10px;
}

.model-icon {
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  overflow: hidden;
  color: var(--cyan);
  font-size: 12px;
  font-weight: 800;
  background: rgba(79, 134, 255, 0.14);
  border: 1px solid rgba(133, 164, 255, 0.16);
}

.model-icon.small {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  font-size: 10px;
  box-shadow: none;
}

.model-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.model-name {
  font-weight: 700;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.delete-btn {
  color: #ff93a4;
}

.delete-btn:hover {
  color: #ffb6c1;
}

.list-cards {
  display: grid;
  gap: 14px;
}

.mobile-only { display: none; }

@media (max-width: 760px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .supplier-models-page {
    padding: 12px 12px 28px;
  }
  .table-panel {
    padding: 10px;
    overflow-x: visible;
  }
  .toolbar {
    flex-wrap: wrap;
  }
  .search-input {
    flex: 1;
    min-width: 80px;
  }
  .pagination-bar {
    text-align: center;
  }
}
.list-card {
  padding: 16px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(27,39,58,.92), rgba(20,30,46,.88));
  border: 1px solid rgba(133,164,255,.12);
  box-shadow: 0 8px 28px rgba(0,0,0,.22);
  overflow: hidden;
  transition: transform .18s ease, box-shadow .18s ease;
}
.list-card:active {
  transform: translateY(-1px);
  box-shadow: 0 12px 36px rgba(0,0,0,.3);
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 8px;
}
.card-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.3;
}
.card-sub {
  font-size: 12px;
  color: rgba(255,255,255,.4);
  margin-top: 2px;
}
.price-grid {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}
.price-cell {
  flex: 1;
  min-width: 0;
  text-align: center;
  padding: 8px 6px;
  border-radius: 8px;
  background: rgba(255,255,255,.04);
}
.price-cell .label {
  font-size: 10px;
  color: rgba(255,255,255,.35);
  letter-spacing: .02em;
}
.price-cell .value {
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  margin-top: 2px;
}
.price-cell .value.input { color: #32c8f5; }
.price-cell .value.output { color: #f4b74f; }
.price-cell .value.cache { color: #34c98f; }
.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(133,164,255,0.06);
  margin-top: 6px;
  font-size: 11px;
  color: rgba(255,255,255,.35);
}
.card-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-top: 10px;
  border-top: 1px solid rgba(133,164,255,0.06);
  margin-top: 6px;
}
.card-actions .el-button--text {
  font-size: 12px;
  padding: 4px 10px;
  height: auto;
}
</style>
